import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ChefHat, ShoppingCart, BookOpen, AlertTriangle, Plus, Trash2, Check, X,
  Save, Calendar, Users, Utensils, FileText, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  getKitchenDietary, getShoppingList, createShoppingItem, updateShoppingItem,
  deleteShoppingItem, clearPurchasedShopping, getKitchenMenus, getRetreat, updateRetreat, fmtDate, fmtDateTime,
} from "@/lib/api";

const TABS = [
  { value: "dashboard", label: "Dashboard", icon: ChefHat },
  { value: "dietary",   label: "Dietary",   icon: Utensils },
  { value: "shopping",  label: "Shopping",  icon: ShoppingCart },
  { value: "menus",     label: "Retreat Menus", icon: BookOpen },
];

export default function Kitchen() {
  const [tab, setTab] = useState("dashboard");
  const [dietary, setDietary] = useState([]);
  const [shopping, setShopping] = useState([]);
  const [menus, setMenus] = useState([]);
  const [activeMenu, setActiveMenu] = useState(null); // {id, name, ...} when editing

  const loadAll = async () => {
    try {
      const [d, s, m] = await Promise.all([
        getKitchenDietary(), getShoppingList(), getKitchenMenus(),
      ]);
      setDietary(d); setShopping(s); setMenus(m);
    } catch { toast.error("Failed to load kitchen data"); }
  };
  useEffect(() => { loadAll(); }, []);

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="kitchen-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <ChefHat size={26} strokeWidth={1.7} className="text-amber-600" /> Kitchen
        </h1>
        <p className="text-sm text-slate-500 mt-1">Dietary requirements, shopping list and retreat menus</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="kitchen-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.value;
          return (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${active ? "border-amber-500 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-900"}`}
              data-testid={`kitchen-tab-${t.value}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "dashboard" && <Dashboard dietary={dietary} shopping={shopping} menus={menus} onJumpTab={setTab} />}
      {tab === "dietary"   && <DietaryList dietary={dietary} />}
      {tab === "shopping"  && <ShoppingList shopping={shopping} reload={loadAll} />}
      {tab === "menus"     && <MenusList menus={menus} onOpen={setActiveMenu} />}

      {activeMenu && (
        <MenuEditor
          retreat={activeMenu}
          onClose={() => setActiveMenu(null)}
          onSaved={() => { setActiveMenu(null); loadAll(); }}
        />
      )}
    </div>
  );
}

// ============================================================================
// DASHBOARD
// ============================================================================
function Dashboard({ dietary, shopping, menus, onJumpTab }) {
  const today = dietary.filter(g => g.in_house_today);
  const pendingShopping = shopping.filter(i => !i.purchased);
  const upcomingRetreats = menus.filter(m => m.is_upcoming).slice(0, 6);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Today's dietary */}
      <Card className="shadow-none border-slate-200 p-4 col-span-1 lg:col-span-1" data-testid="kitchen-dash-dietary">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-1.5"><Utensils size={15} className="text-slate-500" /> Today&apos;s dietary</h3>
          <button onClick={() => onJumpTab("dietary")} className="text-xs text-blue-600 hover:underline">All →</button>
        </div>
        {today.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">No special diets in-house today</div>
        ) : (
          <div className="space-y-2">
            {today.map(g => <DietaryRow key={g.id} g={g} compact />)}
          </div>
        )}
      </Card>

      {/* Pending shopping */}
      <Card className="shadow-none border-slate-200 p-4" data-testid="kitchen-dash-shopping">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-1.5"><ShoppingCart size={15} className="text-slate-500" /> Shopping list</h3>
          <button onClick={() => onJumpTab("shopping")} className="text-xs text-blue-600 hover:underline">All →</button>
        </div>
        {pendingShopping.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">Nothing pending. Nice.</div>
        ) : (
          <ul className="space-y-1.5 max-h-[300px] overflow-auto">
            {pendingShopping.slice(0, 12).map(i => (
              <li key={i.id} className="flex items-center gap-2 text-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                <span className="text-slate-700 truncate">{i.name}</span>
                {i.quantity && <span className="text-xs text-slate-400 ml-auto shrink-0">{i.quantity}</span>}
              </li>
            ))}
            {pendingShopping.length > 12 && (
              <li className="text-xs text-slate-400 pt-1">+ {pendingShopping.length - 12} more</li>
            )}
          </ul>
        )}
      </Card>

      {/* Upcoming retreats */}
      <Card className="shadow-none border-slate-200 p-4" data-testid="kitchen-dash-menus">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-slate-900 flex items-center gap-1.5"><BookOpen size={15} className="text-slate-500" /> Upcoming retreats</h3>
          <button onClick={() => onJumpTab("menus")} className="text-xs text-blue-600 hover:underline">All →</button>
        </div>
        {upcomingRetreats.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">No upcoming retreats</div>
        ) : (
          <ul className="space-y-2">
            {upcomingRetreats.map(r => (
              <li key={r.id} className="text-sm">
                <div className="font-medium text-slate-900 truncate">{r.name}</div>
                <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                  <span>{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span>
                  <Users size={10} className="text-slate-400" /> {r.participants}
                  {r.menu_present && <Badge variant="outline" className="border-emerald-300 text-emerald-700 text-[10px] h-4 px-1.5">menu</Badge>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

// ============================================================================
// DIETARY
// ============================================================================
function DietaryRow({ g, compact }) {
  const severe = g.severe_allergy;
  return (
    <div
      className={`flex items-start gap-3 py-2 px-3 rounded-md border ${severe ? "border-red-200 bg-red-50/40" : "border-slate-100 bg-white"}`}
      data-testid={`dietary-row-${g.id}`}
    >
      {severe && <AlertTriangle size={14} className="text-red-600 mt-0.5 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-900 truncate">{g.guest_name}</span>
          {severe && <Badge className="bg-red-600 hover:bg-red-600 text-white text-[10px] h-4 px-1.5">Severe allergy</Badge>}
          {g.in_house_today && <Badge variant="outline" className="border-emerald-300 text-emerald-700 text-[10px] h-4 px-1.5">In-house</Badge>}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
          {!compact && <span>{g.unit_name}</span>}
          <span>{fmtDate(g.check_in)} → {fmtDate(g.check_out)}</span>
        </div>
        <div className={`text-sm mt-1 ${severe ? "text-red-900 font-medium" : "text-slate-700"}`}>{g.dietary_notes}</div>
      </div>
    </div>
  );
}

function DietaryList({ dietary }) {
  const inHouse = dietary.filter(g => g.in_house_today);
  const upcoming = dietary.filter(g => !g.in_house_today);
  return (
    <div className="space-y-4">
      <Card className="shadow-none border-slate-200 p-4" data-testid="kitchen-dietary-card">
        <h3 className="font-heading font-semibold text-slate-900 mb-3">In-house today <span className="text-slate-400 font-normal">({inHouse.length})</span></h3>
        {inHouse.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">No guests with dietary requirements are in-house today.</div>
        ) : (
          <div className="space-y-2">{inHouse.map(g => <DietaryRow key={g.id} g={g} />)}</div>
        )}
      </Card>
      <Card className="shadow-none border-slate-200 p-4">
        <h3 className="font-heading font-semibold text-slate-900 mb-3">Upcoming <span className="text-slate-400 font-normal">({upcoming.length})</span></h3>
        {upcoming.length === 0 ? (
          <div className="text-sm text-slate-400 py-4 text-center">No upcoming guests with dietary requirements.</div>
        ) : (
          <div className="space-y-2">{upcoming.map(g => <DietaryRow key={g.id} g={g} />)}</div>
        )}
      </Card>
    </div>
  );
}

// ============================================================================
// SHOPPING LIST
// ============================================================================
function ShoppingList({ shopping, reload }) {
  const [draft, setDraft] = useState({ name: "", quantity: "", notes: "" });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  const pending = useMemo(() => shopping.filter(i => !i.purchased), [shopping]);
  const purchased = useMemo(() => shopping.filter(i => i.purchased), [shopping]);

  const add = async () => {
    if (!draft.name.trim()) return toast.error("Item name required");
    try {
      await createShoppingItem({ name: draft.name.trim(), quantity: draft.quantity.trim(), notes: draft.notes.trim() });
      setDraft({ name: "", quantity: "", notes: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add item"); }
  };

  const togglePurchased = async (item) => {
    try { await updateShoppingItem(item.id, { purchased: !item.purchased }); reload(); }
    catch { toast.error("Failed to update item"); }
  };

  const saveEdit = async () => {
    if (!editDraft?.name?.trim()) return toast.error("Name required");
    try {
      await updateShoppingItem(editDraft.id, {
        name: editDraft.name.trim(),
        quantity: editDraft.quantity || "",
        notes: editDraft.notes || "",
      });
      setEditingId(null);
      setEditDraft(null);
      reload();
    } catch { toast.error("Failed to update item"); }
  };

  const remove = async (id) => {
    try { await deleteShoppingItem(id); reload(); }
    catch { toast.error("Failed to delete"); }
  };

  const clearDone = async () => {
    if (purchased.length === 0) return;
    try {
      const res = await clearPurchasedShopping();
      toast.success(`Cleared ${res.deleted} purchased item${res.deleted === 1 ? "" : "s"}`);
      reload();
    } catch { toast.error("Failed to clear"); }
  };

  return (
    <Card className="shadow-none border-slate-200 p-4" data-testid="kitchen-shopping-card">
      {/* Quick add row */}
      <div className="grid grid-cols-[1fr_120px_1fr_auto] gap-2 pb-3 border-b border-slate-200">
        <Input
          placeholder="Item name…"
          value={draft.name}
          onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && add()}
          className="h-10"
          data-testid="shopping-add-name"
        />
        <Input
          placeholder="Qty"
          value={draft.quantity}
          onChange={(e) => setDraft(d => ({ ...d, quantity: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && add()}
          className="h-10"
          data-testid="shopping-add-qty"
        />
        <Input
          placeholder="Notes (optional)"
          value={draft.notes}
          onChange={(e) => setDraft(d => ({ ...d, notes: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && add()}
          className="h-10"
          data-testid="shopping-add-notes"
        />
        <Button onClick={add} className="h-10 bg-amber-600 hover:bg-amber-700 text-white" data-testid="shopping-add-btn">
          <Plus size={14} /> Add
        </Button>
      </div>

      {/* Pending list */}
      <div className="mt-3 space-y-1.5">
        {pending.length === 0 && (
          <div className="text-sm text-slate-400 py-6 text-center">No items pending — well stocked.</div>
        )}
        {pending.map(i => (
          <ShoppingRow
            key={i.id}
            item={i}
            editing={editingId === i.id}
            editDraft={editDraft}
            setEditDraft={setEditDraft}
            onStartEdit={() => { setEditingId(i.id); setEditDraft({ ...i }); }}
            onSaveEdit={saveEdit}
            onCancelEdit={() => { setEditingId(null); setEditDraft(null); }}
            onTogglePurchased={() => togglePurchased(i)}
            onDelete={() => remove(i.id)}
          />
        ))}
      </div>

      {/* Purchased */}
      {purchased.length > 0 && (
        <div className="mt-5 pt-3 border-t border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-wider text-slate-500">Purchased ({purchased.length})</div>
            <button onClick={clearDone} className="text-xs text-slate-500 hover:text-red-600 flex items-center gap-1" data-testid="shopping-clear-done-btn">
              <Trash2 size={11} /> Clear done
            </button>
          </div>
          <div className="space-y-1.5">
            {purchased.map(i => (
              <ShoppingRow
                key={i.id}
                item={i}
                editing={false}
                onTogglePurchased={() => togglePurchased(i)}
                onDelete={() => remove(i.id)}
              />
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}

function ShoppingRow({ item, editing, editDraft, setEditDraft, onStartEdit, onSaveEdit, onCancelEdit, onTogglePurchased, onDelete }) {
  if (editing) {
    return (
      <div className="grid grid-cols-[1fr_120px_1fr_auto] gap-2 items-center" data-testid={`shopping-edit-${item.id}`}>
        <Input value={editDraft.name} onChange={(e) => setEditDraft(d => ({ ...d, name: e.target.value }))} className="h-9" data-testid="shopping-edit-name" />
        <Input value={editDraft.quantity || ""} onChange={(e) => setEditDraft(d => ({ ...d, quantity: e.target.value }))} className="h-9" />
        <Input value={editDraft.notes || ""} onChange={(e) => setEditDraft(d => ({ ...d, notes: e.target.value }))} className="h-9" />
        <div className="flex items-center gap-1">
          <button onClick={onSaveEdit} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded" data-testid="shopping-edit-save"><Check size={14} /></button>
          <button onClick={onCancelEdit} className="p-2 text-slate-400 hover:bg-slate-50 rounded"><X size={14} /></button>
        </div>
      </div>
    );
  }
  return (
    <div className={`flex items-center gap-3 px-2 py-1.5 rounded hover:bg-slate-50 group ${item.purchased ? "opacity-60" : ""}`} data-testid={`shopping-row-${item.id}`}>
      <input
        type="checkbox"
        checked={item.purchased}
        onChange={onTogglePurchased}
        className="accent-emerald-500 h-4 w-4 cursor-pointer shrink-0"
        data-testid={`shopping-check-${item.id}`}
      />
      <div className="flex-1 min-w-0">
        <div className={`text-sm text-slate-800 truncate ${item.purchased ? "line-through" : ""}`}>{item.name}</div>
        {item.notes && <div className="text-xs text-slate-500 truncate">{item.notes}</div>}
      </div>
      {item.quantity && <span className="text-xs text-slate-500 tabular-nums px-2 py-0.5 bg-slate-100 rounded shrink-0">{item.quantity}</span>}
      {!item.purchased && (
        <button onClick={onStartEdit} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-700 transition-opacity p-1" data-testid={`shopping-edit-btn-${item.id}`}>
          <FileText size={12} />
        </button>
      )}
      <button onClick={onDelete} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition-opacity p-1" data-testid={`shopping-delete-${item.id}`}>
        <Trash2 size={12} />
      </button>
    </div>
  );
}

// ============================================================================
// RETREAT MENUS
// ============================================================================
function MenusList({ menus, onOpen }) {
  const upcoming = menus.filter(m => m.is_upcoming);
  const past = menus.filter(m => !m.is_upcoming);

  return (
    <div className="space-y-4">
      <Card className="shadow-none border-slate-200" data-testid="kitchen-menus-card">
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="font-heading font-semibold text-slate-900">Upcoming retreats <span className="text-slate-400 font-normal">({upcoming.length})</span></h3>
        </div>
        {upcoming.length === 0 ? (
          <div className="p-6 text-sm text-slate-400 text-center">No upcoming retreats.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {upcoming.map(r => <MenuRow key={r.id} r={r} onOpen={() => onOpen(r)} />)}
          </div>
        )}
      </Card>

      {past.length > 0 && (
        <Card className="shadow-none border-slate-200">
          <div className="px-4 py-3 border-b border-slate-200">
            <h3 className="font-heading font-semibold text-slate-900 text-slate-500">Past retreats <span className="font-normal">({past.length})</span></h3>
          </div>
          <div className="divide-y divide-slate-100">
            {past.map(r => <MenuRow key={r.id} r={r} onOpen={() => onOpen(r)} />)}
          </div>
        </Card>
      )}
    </div>
  );
}

function MenuRow({ r, onOpen }) {
  return (
    <div className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50/50 cursor-pointer group" onClick={onOpen} data-testid={`menu-row-${r.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-900 truncate">{r.name}</div>
        <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5 flex-wrap">
          {r.partner_name && <span>{r.partner_name}</span>}
          <span className="flex items-center gap-1"><Calendar size={11} /> {fmtDate(r.start_date)} → {fmtDate(r.end_date)}</span>
          <span className="flex items-center gap-1"><Users size={11} /> {r.participants}</span>
          {r.menu_present && <Badge variant="outline" className="border-emerald-300 text-emerald-700 text-[10px] h-4 px-1.5">menu set</Badge>}
        </div>
      </div>
      {r.menu_updated_at && (
        <div className="text-xs text-slate-400 shrink-0">Updated {fmtDateTime(r.menu_updated_at)}</div>
      )}
      <ArrowRight size={14} className="text-slate-300 group-hover:text-slate-600 shrink-0" />
    </div>
  );
}

function MenuEditor({ retreat, onClose, onSaved }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Fetch the full retreat to get the menu body.
    (async () => {
      try {
        const data = await getRetreat(retreat.id);
        setText(data.menu || "");
      } catch { toast.error("Failed to load menu"); }
      setLoading(false);
    })();
  }, [retreat.id]);

  const save = async () => {
    setSaving(true);
    try {
      await updateRetreat(retreat.id, { menu: text });
      toast.success("Menu saved");
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="menu-editor-overlay">
      <div className="bg-white rounded-md shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="menu-editor">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <div className="font-heading font-semibold text-lg text-slate-900">{retreat.name}</div>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1"><Calendar size={11} /> {fmtDate(retreat.start_date)} → {fmtDate(retreat.end_date)}</span>
              <span className="flex items-center gap-1"><Users size={11} /> {retreat.participants}</span>
              {retreat.menu_updated_at && <span>Last updated {fmtDateTime(retreat.menu_updated_at)}</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1" data-testid="menu-editor-close"><X size={16} /></button>
        </div>
        <div className="p-5 flex-1 overflow-auto">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste menu from Word, type freely…"
            className="min-h-[400px] font-mono text-sm leading-relaxed"
            disabled={loading}
            data-testid="menu-editor-textarea"
          />
          <div className="text-xs text-slate-400 mt-2">Tip: paste directly from Google Docs or Word — line breaks are preserved.</div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="menu-editor-save">
            <Save size={14} /> {saving ? "Saving…" : "Save menu"}
          </Button>
        </div>
      </div>
    </div>
  );
}
